import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Box, Typography, Grid } from "@mui/material";
import { User } from "@/interfaces/User";
import { IconX } from "@tabler/icons-react";
import { Button } from "./ui/button";
import { Eye, EyeOff } from "lucide-react";
import { useAppDispatch } from "@/redux/hooks";
import { getUser, updateUser } from "@/redux/user/user.action";
import { Input } from "./ui/input";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { SuccessToast } from "@/utilities/toasts/SuccessToast";
import { ErrorToast } from "@/utilities/toasts/ErrorToast";
import { userSelector } from "@/redux/user/user.selector";
import { useSelector } from "react-redux";

interface DialogProfileProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  userInfo: User;
}

const FormSchema = z
  .object({
    password: z.string().min(8, {
      message: "La contraseña debe tener al menos 8 caracteres.",
    }),
    confirmPassword: z.string().min(8, {
      message: "La confirmación debe tener al menos 8 caracteres.",
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Las contraseñas no coinciden.",
    path: ["confirmPassword"],
  });

const DialogPassword = ({ open, setOpen, userInfo }: DialogProfileProps) => {
  const dispatch = useAppDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { loading } = useSelector(userSelector).ui;
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });
  useEffect(() => {
    dispatch(getUser());
  }, [dispatch]);

  const onSubmit = async (data: z.infer<typeof FormSchema>) => {
    const result = await dispatch(
      updateUser({
        user: { password: data.password },
        userId: userInfo.id,
      }),
    );
    if (result.meta.requestStatus === "fulfilled") {
      await dispatch(getUser());
      SuccessToast({ message: "Contraseña actualizada correctamente" });
      form.reset();
      setShowPassword(false);
      setShowConfirmPassword(false);
      setOpen(false);
    } else {
      ErrorToast({ message: "Error al actualizar la contraseña" });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={() => {
        form.reset();
        setShowPassword((prev) => !prev);
        setShowConfirmPassword((prev) => !prev);
        setOpen((prev) => !prev);
      }}
    >
      <VisuallyHidden>
        <DialogTitle>PasswordConfig</DialogTitle>
      </VisuallyHidden>
      <DialogContent
        className="bg-sidebar min-w-[15rem] w-[30rem]"
        aria-describedby={undefined}
      >
        <DialogHeader className="flex pb-0 h-fit">
          <Box className="flex justify-end w-full font-bold">
            <Button
              className="p-0 text-[#1F6B45] h-fit"
              onClick={() => {
                form.reset();
                setOpen(false);
              }}
            >
              <IconX className="p-0" />
            </Button>
          </Box>
          <DialogTitle>
            <Typography
              fontFamily={"Inter"}
              fontSize={"18px"}
              fontWeight={500}
              letterSpacing={0}
            >
              Cambiar contraseña
            </Typography>
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            Cambia tu contraseña actual por una nueva para mantener tu cuenta
            segura.
          </DialogDescription>
        </DialogHeader>
        <Grid container gap={1} className="w-full flex justify-between">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="w-full space-y-6 flex flex-col gap-4"
            >
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem className="max-h-[3.6rem]">
                    <FormLabel>Nueva contraseña</FormLabel>
                    <FormControl>
                      <Box
                        sx={{
                          minWidth: "100%",
                          maxWidth: "100%",
                          position: "relative",
                        }}
                      >
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="Nueva contraseña"
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((prev) => !prev)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                          aria-label="Toggle password visibility"
                        >
                          {showPassword ? (
                            <EyeOff className="size-5 text-[#1F6B45]" />
                          ) : (
                            <Eye className="size-5 text-[#1F6B45]" />
                          )}
                        </button>
                      </Box>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem className="max-h-[3.6rem]">
                    <FormLabel>Confirmar contraseña</FormLabel>
                    <FormControl>
                      <Box
                        sx={{
                          minWidth: "100%",
                          maxWidth: "100%",
                          position: "relative",
                        }}
                      >
                        <Input
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Confirmar contraseña"
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword((prev) => !prev)
                          }
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                          aria-label="Toggle password visibility"
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="size-5 text-[#1F6B45]" />
                          ) : (
                            <Eye className="size-5 text-[#1F6B45]" />
                          )}
                        </button>
                      </Box>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter
                className={`flex w-full gap-4 pt-4 flex-row items-center justify-center flex-wrap sm:flex-row sm:justify-end sm:items-center`}
              >
                <Button
                  variant="cancel"
                  className="w-full justify-self-start w-[7.5rem] flex"
                  onClick={() => {
                    form.reset();
                    setShowPassword(false);
                    setShowConfirmPassword(false);
                    setOpen(false);
                  }}
                  disabled={loading}
                >
                  <Typography
                    fontFamily={"Inter"}
                    fontSize={"14px"}
                    fontWeight={500}
                  >
                    Cancelar
                  </Typography>
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  className="w-full justify-self-end w-[7.5rem] flex hover:bg-[#1F6B45]/90"
                  disabled={loading}
                >
                  <Typography
                    fontFamily={"Inter"}
                    fontSize={"14px"}
                    fontWeight={500}
                  >
                    Confirmar
                  </Typography>
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default DialogPassword;
